import { useState } from "react";
import type { CSSProperties } from "react";
import type { ScreenId } from "@/admin/catalog";
import { AdminErrorState } from "@/components/admin/AdminErrorState";
import { SettingRow } from "@/components/admin/SettingRow";
import { useAdminT } from "@/components/admin/useAdminT";
import { DialEditor } from "@/components/admin/editors/DialEditor";
import { FlagEditor } from "@/components/admin/editors/FlagEditor";
import { FreeTierEditor } from "@/components/admin/editors/FreeTierEditor";
import { LlmEditor } from "@/components/admin/editors/LlmEditor";
import { useScreenSettings } from "../useScreenSettings";
import type { ScreenSetting } from "../useScreenSettings";
import { useFocusHighlight } from "./useFocusHighlight";

const SECTION: CSSProperties = { display: "flex", flexDirection: "column", gap: 16 };
const H2: CSSProperties = { fontFamily: "var(--font-display)", fontSize: 22, margin: 0 };
const H3: CSSProperties = {
  fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--d-fg-muted)", margin: 0,
  textTransform: "uppercase",
};
const GROUP: CSSProperties = { display: "flex", flexDirection: "column", gap: 12 };
const FINE_BTN: CSSProperties = {
  alignSelf: "flex-start", background: "transparent", border: "1px solid var(--d-line)",
  borderRadius: 999, padding: "4px 12px", color: "var(--d-fg-muted)",
  fontFamily: "var(--font-body)", fontSize: 12, cursor: "pointer",
};
const LABEL: CSSProperties = {
  fontFamily: "var(--font-body)", fontSize: 12, color: "var(--d-fg-muted)",
};

/** Catalogue group of rows the catalogue does not know. */
const OTHER = "Other";

function SettingEditor({ s }: { s: ScreenSetting }): JSX.Element {
  const t = useAdminT();
  const name = s.entry?.name ?? s.key;
  const risk = s.entry?.risk ?? "caution";
  switch (s.kind) {
    case "dial":
      return <DialEditor settingKey={s.key} name={name} risk={risk} row={s.row} />;
    case "flag":
      return <FlagEditor settingKey={s.key} name={name} risk={risk} flag={s.row} />;
    case "llm":
      return s.row ? (
        <LlmEditor settingKey={s.key} name={name} risk={risk} row={s.row} />
      ) : (
        <span style={LABEL}>{t("shell.not_set")}</span>
      );
    case "free_tier":
      return s.value === undefined ? (
        <span style={LABEL}>{t("shell.not_set")}</span>
      ) : (
        <FreeTierEditor settingKey={s.key} name={name} risk={risk} value={s.value} />
      );
  }
}

interface Group {
  name: string;
  main: ScreenSetting[];
  fine: ScreenSetting[];
}

/** Groups in first-appearance order; the main switch (a flag) leads its group. */
function groupSettings(settings: ScreenSetting[]): Group[] {
  const groups = new Map<string, Group>();
  for (const s of settings) {
    const name = s.entry?.group ?? OTHER;
    let g = groups.get(name);
    if (!g) {
      g = { name, main: [], fine: [] };
      groups.set(name, g);
    }
    (s.entry?.fineTuning ? g.fine : g.main).push(s);
  }
  for (const g of groups.values()) {
    g.main.sort((a, b) => Number(b.kind === "flag") - Number(a.kind === "flag"));
  }
  return [...groups.values()];
}

function GroupBlock({ group, focus }: { group: Group; focus: string | null }): JSX.Element {
  const t = useAdminT();
  const hasFocus = group.fine.some((s) => s.key === focus);
  const [open, setOpen] = useState(hasFocus);
  const row = (s: ScreenSetting) => (
    <SettingRow key={`${s.kind}:${s.key}`} settingKey={s.key} entry={s.entry}>
      <SettingEditor s={s} />
    </SettingRow>
  );
  return (
    <div style={GROUP} data-testid={`group-${group.name}`}>
      <h3 style={H3}>{group.name}</h3>
      {group.main.map(row)}
      {group.fine.length > 0 && (
        <>
          <button
            type="button"
            style={FINE_BTN}
            aria-expanded={open || hasFocus}
            onClick={() => setOpen((v) => !v)}
          >
            {t("shell.fine_tuning")} ({group.fine.length})
          </button>
          {(open || hasFocus) && group.fine.map(row)}
        </>
      )}
    </div>
  );
}

/** «Settings» section of a screen: all dials, flags, LLM rows and limits of the catalogue entry set. */
export function SettingsSection({ screen }: { screen: ScreenId }): JSX.Element | null {
  const t = useAdminT();
  const { settings, isLoading, isError, refetch } = useScreenSettings(screen);
  const focus = useFocusHighlight(!isLoading && !isError);
  if (settings.length === 0 && !isLoading && !isError) return null;
  return (
    <section style={SECTION}>
      <h2 style={H2}>Settings</h2>
      {isError ? (
        <AdminErrorState onRetry={refetch} />
      ) : isLoading ? (
        <span style={LABEL}>{t("common.loading")}</span>
      ) : (
        groupSettings(settings).map((g) => <GroupBlock key={g.name} group={g} focus={focus} />)
      )}
    </section>
  );
}
